import { useState, useEffect, useCallback } from 'react';
import { showToast } from '@/shared/utils/toast';
import { reduxLogger } from '@/core/utils/logger';

interface PushSubscriptionState {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  permission: NotificationPermission;
  subscription: PushSubscription | null;
  error: string | null;
}

interface DeviceInfo {
  deviceName: string;
  browser: string;
  operatingSystem: string;
}

export const usePushNotifications = () => {
  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    isSubscribed: false,
    isLoading: false,
    permission: 'default',
    subscription: null,
    error: null
  });

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const isSupported = 
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;
      
      setState(prev => ({
        ...prev,
        isSupported,
        permission: isSupported ? Notification.permission : 'default'
      }));

      if (!isSupported) {
        reduxLogger.warn('Push notifications are not supported in this browser');
      }
    };

    checkSupport();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (!state.isSupported) {
      setState(prev => ({ ...prev, error: 'Push notifications not supported' }));
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      reduxLogger.info('Service Worker registered successfully');
      return registration;
    } catch (error) {
      reduxLogger.error('Service Worker registration failed:', error);
      setState(prev => ({ ...prev, error: 'Failed to register service worker' }));
      return null;
    }
  }, [state.isSupported]);

  // Get device information
  const getDeviceInfo = (): DeviceInfo => {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let operatingSystem = 'Unknown';
    
    // Detect browser
    if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
    } else if (userAgent.includes('Chrome')) {
      if (userAgent.includes('Edg')) {
        browser = 'Edge';
      } else {
        browser = 'Chrome';
      }
    } else if (userAgent.includes('Safari')) {
      browser = 'Safari';
    }

    // Detect OS
    if (userAgent.includes('Windows')) {
      operatingSystem = 'Windows';
    } else if (userAgent.includes('Mac')) {
      operatingSystem = 'macOS';
    } else if (userAgent.includes('Linux')) {
      operatingSystem = 'Linux';
    } else if (userAgent.includes('Android')) {
      operatingSystem = 'Android';
    } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      operatingSystem = 'iOS';
    }

    // Create a device name
    const deviceName = `${browser} on ${operatingSystem}`;

    return {
      deviceName,
      browser,
      operatingSystem
    };
  };

  // Convert VAPID key from base64 to Uint8Array
  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Check current subscription status
  const checkSubscription = useCallback(async (): Promise<void> => {
    if (!state.isSupported) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      setState(prev => ({
        ...prev,
        isSubscribed: !!subscription,
        subscription,
        isLoading: false
      }));
    } catch (error) {
      reduxLogger.error('Error checking push subscription:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Failed to check subscription status'
      }));
    }
  }, [state.isSupported]);

  // Initialize on mount
  useEffect(() => {
    if (state.isSupported) {
      queueMicrotask(() => {
        void checkSubscription();
      });
    }
  }, [state.isSupported, checkSubscription]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!state.isSupported) {
      showToast.error('Push notifications are not supported in your browser');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      setState(prev => ({ ...prev, permission }));
      
      if (permission === 'denied') {
        showToast.error('Notification permission denied. Please enable it in your browser settings.');
      }
      
      return permission;
    } catch (error) {
      reduxLogger.error('Error requesting notification permission:', error);
      showToast.error('Failed to request notification permission');
      return 'denied';
    }
  }, [state.isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (vapidPublicKey: string): Promise<PushSubscription | null> => {
    if (!state.isSupported) {
      showToast.error('Push notifications are not supported');
      return null;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Ensure permission is granted
      let permission = state.permission;
      if (permission === 'default') {
        permission = await requestPermission();
        if (permission !== 'granted') {
          setState(prev => ({ ...prev, isLoading: false }));
          return null;
        }
      } else if (permission !== 'granted') {
        showToast.error('Please enable notifications in your browser settings');
        setState(prev => ({ ...prev, isLoading: false }));
        return null;
      }

      // Register service worker if needed
      const registration = await registerServiceWorker();
      if (!registration) {
        setState(prev => ({ ...prev, isLoading: false }));
        return null;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource
      });

      setState(prev => ({
        ...prev,
        isSubscribed: true,
        subscription,
        isLoading: false,
        error: null
      }));

      reduxLogger.info('Successfully subscribed to push notifications');
      return subscription;
    } catch (error) {
      reduxLogger.error('Error subscribing to push notifications:', error);
      const errorMessage = 'Failed to subscribe to push notifications';
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: errorMessage
      }));
      showToast.error(errorMessage);
      return null;
    }
  }, [state.isSupported, state.permission, requestPermission, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!state.subscription) {
      reduxLogger.warn('No active push subscription to unsubscribe from');
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await state.subscription.unsubscribe();
      
      setState(prev => ({
        ...prev,
        isSubscribed: false,
        subscription: null,
        isLoading: false,
        error: null
      }));

      reduxLogger.info('Successfully unsubscribed from push notifications');
      return true;
    } catch (error) {
      reduxLogger.error('Error unsubscribing from push notifications:', error);
      const errorMessage = 'Failed to unsubscribe from push notifications';
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: errorMessage
      }));
      showToast.error(errorMessage);
      return false;
    }
  }, [state.subscription]);

  // Get subscription JSON for sending to server
  const getSubscriptionJSON = useCallback(() => {
    if (!state.subscription) return null;

    const json = state.subscription.toJSON();
    const deviceInfo = getDeviceInfo();

    return {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys?.p256dh || '',
        auth: json.keys?.auth || ''
      },
      deviceInfo
    };
  }, [state.subscription]);

  return {
    ...state,
    requestPermission,
    subscribe,
    unsubscribe,
    checkSubscription,
    getSubscriptionJSON,
    registerServiceWorker
  };
};